import moment from 'moment';

import { stepperMenuUtils } from 'components/createBooking/stepperMenu/stepperMenuUtils';
import { BOOKING_STATUS, BOOKING_TYPE, TASK_BASED_STUDY_TYPES } from 'lib/constants';

import type { BookingConfig, OnlineTaskBookingConfig } from 'containers/Booking/types';

export function isBookingForProfessionals(
  booking?: { config?: { participant_category?: number | null } | null } | null,
): boolean {
  return booking?.config?.participant_category === 2;
}

export const enum BookingTypes {
  IN_PERSON = 1,
  REMOTE = 2,
  ONLINE = 3,
  LONGITUDINAL_STUDY = 4,
  UNMODERATED = 5,
  AI_MODERATED = 6,
  SURVEY = 7,
}

export function isModeratedBooking(type?: BookingTypes | null): boolean {
  return type === BookingTypes.IN_PERSON || type === BookingTypes.REMOTE;
}

type SelfSchedulingFields = {
  type?: number | null;
  config?: { is_self_scheduling_enabled?: boolean | null } | null;
};

/** A remote interview where participants pick their own time. It has no session until a participant books one. */
export function isSelfScheduled(booking: SelfSchedulingFields): boolean {
  return booking.type === BookingTypes.REMOTE && booking.config?.is_self_scheduling_enabled === true;
}

/** A Video Call study from the studies builder. The booking sets its route, not the feature flag. */
export function isVideoCallStudy(booking: {
  type?: number | null;
  config?: { builder?: string | null } | null;
}): boolean {
  return booking.type === BookingTypes.REMOTE && booking.config?.builder === 'studies';
}

/** A Video Call draft opens on Schedule, its first tab. Other statuses open Recruit participants. */
export function getVideoCallLandingPath(status: number | null | undefined): string {
  return status === BOOKING_STATUS.DRAFT ? 'schedule' : 'recruit/participants';
}

/** A Video Call previews as its opportunity listing, which participants can only open while it is active. */
export function hasStudyPreview(booking: Parameters<typeof isVideoCallStudy>[0] & { status?: number | null }): boolean {
  return (
    TASK_BASED_STUDY_TYPES.some(type => type === booking.type) ||
    (isVideoCallStudy(booking) && booking.status === BOOKING_STATUS.ACTIVE)
  );
}

export type StudyPreviewKind = 'slots' | 'link' | null;

/** A self-scheduled study previews its free times in any status, so a draft can be checked before launch. */
export function getStudyPreviewKind(
  booking: Parameters<typeof hasStudyPreview>[0] & SelfSchedulingFields,
  { isModeratedSchedulingEnabled }: { isModeratedSchedulingEnabled: boolean },
): StudyPreviewKind {
  if (isModeratedSchedulingEnabled && isSelfScheduled(booking)) {
    return 'slots';
  }
  return hasStudyPreview(booking) ? 'link' : null;
}

/** A scheduled study whose participants are invited to a session that already exists. */
export function hasSessionAtInvite(booking: SelfSchedulingFields): boolean {
  const isScheduled = isModeratedBooking(booking.type) || booking.type === BookingTypes.LONGITUDINAL_STUDY;
  return isScheduled && !isSelfScheduled(booking);
}

export function isAutomatedBooking(config?: Pick<BookingConfig, 'options'> | null): boolean {
  return config?.options?.review_submission === false;
}

export function isBookingFull({
  bookingTotalParticipants,
  currentBookingTotalApplicants,
}: {
  bookingTotalParticipants: number;
  currentBookingTotalApplicants: number;
}) {
  return currentBookingTotalApplicants >= bookingTotalParticipants;
}

export enum RESOLVED_BOOKING_TYPES {
  IN_PERSON = 'IN_PERSON',
  VIDEO_CALL = 'VIDEO_CALL',
  ONLINE_TASK = 'ONLINE_TASK',
  SURVEY = 'SURVEY',
  LONGITUDINAL_STUDY = 'LONGITUDINAL_STUDY',
  AI_MODERATED = 'AI_MODERATED',
  UNMODERATED = 'UNMODERATED',
}

export const getResolvedBookingType = (
  bookingType: number | null | undefined,
  onlineTaskType: OnlineTaskBookingConfig['type'] | undefined,
): RESOLVED_BOOKING_TYPES => {
  switch (bookingType) {
    case BOOKING_TYPE.ONLINE: {
      if (onlineTaskType === 2) {
        return RESOLVED_BOOKING_TYPES.SURVEY;
      }
      if (onlineTaskType === 3) {
        return RESOLVED_BOOKING_TYPES.AI_MODERATED;
      }
      return RESOLVED_BOOKING_TYPES.ONLINE_TASK;
    }
    case BOOKING_TYPE.REMOTE:
      return RESOLVED_BOOKING_TYPES.VIDEO_CALL;
    case BOOKING_TYPE.IN_PERSON:
      return RESOLVED_BOOKING_TYPES.IN_PERSON;
    case BOOKING_TYPE.UNMODERATED:
      return RESOLVED_BOOKING_TYPES.UNMODERATED;
    case BOOKING_TYPE.AI_MODERATED:
      return RESOLVED_BOOKING_TYPES.AI_MODERATED;
    case BOOKING_TYPE.SURVEY:
      return RESOLVED_BOOKING_TYPES.SURVEY;
    case BOOKING_TYPE.LONGITUDINAL_STUDY:
    default:
      return RESOLVED_BOOKING_TYPES.LONGITUDINAL_STUDY;
  }
};

export const findNextRoute = (booking: any, currentRouteLabel: string, currentStep: any) => {
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
  let routes = stepperMenuUtils.projectSubSteps(booking);
  if (currentStep === 'project-setup') {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
    routes = stepperMenuUtils.projectSubSteps(booking);
  } else if (currentStep === 'AI_MODERATED') {
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
    routes = stepperMenuUtils.aiModeratedSubSteps(booking);
  }
  const currentRouteIndex = routes.findIndex(route => route.label === currentRouteLabel);
  return routes[currentRouteIndex + 1].link;
};

export const getDemographicAgeDefaultValue = () => {
  const minAge = moment().subtract(18, 'y').valueOf().toString();
  const maxAge = moment().subtract(99, 'y').valueOf().toString();
  return {
    minAge: {
      field: 'user.meta.identity.birthday.timestamp',
      value: minAge,
      operator: 'lt',
    },
    maxAge: {
      field: 'user.meta.identity.birthday.timestamp',
      value: maxAge,
      operator: 'gt',
    },
  };
};
